import React, { useState, useEffect } from "react";
import CheckoutField from "../CheckoutField";
import useStore from "../../../store/useStore";
import toast from "react-hot-toast";
import usePincodeCheck from "../../../utils/usePincodeCheck";
import {
  rules,
} from "./utils/addressValidation";
import CheckoutEditModal from "./components/CheckoutEditModal";
import PincodeStatus from "./components/PincodeStatus";
import AddressForm from "./components/AddressForm";
import SavedAddressCard from "./components/SavedAddressCard";

const getAddressId = (address) => address?.address_id ?? address?.id;

const AddressStep = ({ form, onChange, onNext, onAddressSelect }) => {
  const {
    user_addresses,
    fetchUserAddresses,
    updateAddress,
    deleteAddress,
    addresses_loading,
    isAuthenticated,
  } = useStore();
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAddr, setEditingAddr] = useState(null);
  const [showOtherAddresses, setShowOtherAddresses] = useState(false);
  const [pincodeCityState, setPincodeCityState] = useState({ city: '', state: '' });

  // Separate default and other addresses
  // Prefer is_default flag, fallback to first address
  const defaultAddress = user_addresses.find((a) => a.is_default) || user_addresses[0] || null;
  const otherAddresses = user_addresses.filter((a) => {
    const defId = defaultAddress ? (defaultAddress.address_id ?? defaultAddress.id) : null;
    return String(a.address_id ?? a.id) !== String(defId);
  });

  // Auto-fill city and state for specific pincodes
  useEffect(() => {
    const pincode = form.pincode?.trim();
    if (pincode?.length === 6) {
      // Check for specific pincodes: 411033 and 411028
      if (pincode === '411033' || pincode === '411028') {
        setPincodeCityState({ city: 'Pune', state: 'Maharashtra' });
      } else {
        setPincodeCityState({ city: '', state: '' });
      }
    } else {
      setPincodeCityState({ city: '', state: '' });
    }
  }, [form.pincode]);

  // Update form when pincodeCityState changes
  useEffect(() => {
    if (pincodeCityState.city && pincodeCityState.state && !touched.city && !touched.state) {
      onChange({ target: { name: 'city', value: pincodeCityState.city } });
      onChange({ target: { name: 'state', value: pincodeCityState.state } });
    } else if (!form.pincode || form.pincode.length !== 6) {
      // Clear city and state when pincode is removed or invalid
      if (pincodeCityState.city === '' && pincodeCityState.state === '') {
        onChange({ target: { name: 'city', value: '' } });
        onChange({ target: { name: 'state', value: '' } });
      }
    }
  }, [pincodeCityState, touched.city, touched.state, form.pincode, onChange]);

  // Pincode to check: from selected saved address OR from manual form
  const activePincode = selectedId
    ? user_addresses.find((a) => String(getAddressId(a)) === String(selectedId))?.pincode || ""
    : form.pincode || "";

  const { status: pincodeStatus, message: pincodeMessage, checkNow: checkPincode } =
    usePincodeCheck(activePincode);

  const isPincodeReady = activePincode.length === 6;
  // Block continue only if pincode is 6 digits and explicitly not serviceable or error
  const blockedByPincode =
    isPincodeReady && (pincodeStatus === "not_serviceable" || pincodeStatus === "error");
  const pincodeChecking = isPincodeReady && pincodeStatus === "checking";

  useEffect(() => {
    if (isAuthenticated) fetchUserAddresses();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user_addresses.length > 0 && selectedId === null) {
      const def = user_addresses.find((a) => a.is_default) || user_addresses[0];
      const id = getAddressId(def);
      setSelectedId(id);
      prefillForm(def);
      setShowForm(false);
      if (onAddressSelect) onAddressSelect(id);
    } else if (user_addresses.length === 0 && !addresses_loading) {
      setShowForm(true);
    }
  }, [user_addresses, addresses_loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const prefillForm = (addr) => {
    const fields = {
      addressLine1: addr.address || addr.street || "",
      addressLine2: addr.locality || "",
      city: addr.city || "",
      state: addr.state || "",
      pincode: addr.pincode || "",
    };
    Object.entries(fields).forEach(([name, value]) =>
      onChange({ target: { name, value } }),
    );
  };

  const handleSelectAddress = (addr) => {
    const id = getAddressId(addr);
    setSelectedId(id);
    prefillForm(addr);
    setShowForm(false);
    if (onAddressSelect) onAddressSelect(id);
  };

  const validate = (name, value) => {
    const fn = rules[name];
    return fn ? fn(value) : "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let filtered = value;
    if (name === "city" || name === "state")
      filtered = value.replace(/[^a-zA-Z\s]/g, "");
    else if (name === "pincode")
      filtered = value.replace(/[^0-9]/g, "").slice(0, 6);
    onChange({ target: { name, value: filtered } });
    if (touched[name])
      setErrors((p) => ({ ...p, [name]: validate(name, filtered) }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((p) => ({ ...p, [name]: true }));
    setErrors((p) => ({ ...p, [name]: validate(name, value) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (blockedByPincode) {
      toast.error("Delivery is not available for this pincode.");
      return;
    }
    if (pincodeChecking) {
      toast("Checking pincode serviceability, please wait…", { icon: "⏳" });
      return;
    }
    const fields = ["addressLine1", "city", "state", "pincode"];
    const newErrors = {};
    const newTouched = {};
    fields.forEach((f) => {
      newTouched[f] = true;
      newErrors[f] = validate(f, form[f] || "");
    });
    setTouched(newTouched);
    setErrors(newErrors);
    if (Object.values(newErrors).every((e) => !e)) onNext();
  };

  const handleSaveSuccess = (savedAddr) => {
    setShowForm(false);
    const freshAddresses = useStore.getState().user_addresses;
    const toSelect = savedAddr?.id
      ? freshAddresses.find((a) => String(a.id) === String(savedAddr.id))
      : freshAddresses[freshAddresses.length - 1];
    if (toSelect) {
      const id = getAddressId(toSelect);
      setSelectedId(id);
      prefillForm(toSelect);
      if (onAddressSelect) onAddressSelect(id);
    }
  };

  const handleCancelNewAddress = () => {
    setShowForm(false);
    const fallbackAddress =
      user_addresses.find((a) => String(getAddressId(a)) === String(selectedId)) ||
      defaultAddress ||
      user_addresses[0];

    if (fallbackAddress) {
      handleSelectAddress(fallbackAddress);
    } else {
      setSelectedId(null);
    }
  };

  const handleDeleteAddr = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await deleteAddress(id);
      if (String(selectedId) === String(id)) {
        setSelectedId(null);
      }
      toast.success("Address deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    }
  };

  const handleEditAddrSubmit = async (form) => {
    try {
      await updateAddress(editingAddr.address_id ?? editingAddr.id, form);
      toast.success("Address updated!");
      setEditingAddr(null);
      // Re-select updated address
      const fresh = useStore.getState().user_addresses;
      const updated = fresh.find(
        (a) => String(a.id) === String(editingAddr.id),
      );
      if (updated && String(selectedId) === String(editingAddr.id))
        prefillForm(updated);
    } catch (err) {
      toast.error(err.message || "Failed to update address");
    }
  };

  const field = (name) => ({
    name,
    value: form[name] || "",
    onChange: handleChange,
    onBlur: handleBlur,
    error: errors[name],
  });

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-black/5 sm:p-8">
      <h2 className="mb-6 flex items-center gap-3 text-xl font-extrabold text-gray-900">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-black text-primary-600">
          1
        </span>
        Delivery Address
      </h2>

      {/* ── Default address (always shown at top, pre-selected) ── */}
      {defaultAddress && (
        <div className="mb-6">
          <div className="mb-3 flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500"></div>
            <p className="text-sm font-bold text-gray-900">
              {defaultAddress?.is_default ? 'Default Address' : 'Delivery Address'}
            </p>
            {String(selectedId) === String(defaultAddress.address_id ?? defaultAddress.id) && !showForm && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-600">Selected</span>
            )}
          </div>
          <SavedAddressCard
            addr={defaultAddress}
            selectedId={selectedId}
            showForm={showForm}
            onSelect={() => handleSelectAddress(defaultAddress)}
            onEdit={() => setEditingAddr(defaultAddress)}
            onDelete={() => handleDeleteAddr(defaultAddress.id)}
          />
        </div>
      )}

      {/* ── Other addresses (collapsible) ── */}
      {otherAddresses.length > 0 && (
        <div className="mb-6">
          {/* Collapsible Header */}
          <button
            type="button"
            onClick={() => setShowOtherAddresses(!showOtherAddresses)}
            className="mb-3 flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 transition-all hover:border-primary-300 hover:bg-primary-50"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-gray-900">
                  Saved Addresses ({otherAddresses.length})
                </p>
                <p className="text-xs text-gray-500">
                  {showOtherAddresses ? "Tap to collapse" : "Tap to view"}
                </p>
              </div>
            </div>
            <div className={`transform transition-transform duration-300 ${showOtherAddresses ? "rotate-180" : ""}`}>
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {/* Expandable Address List */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showOtherAddresses ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <>
              <div className="space-y-3">
                {otherAddresses.map((addr) => (
                  <SavedAddressCard
                    key={addr.id}
                    addr={addr}
                    selectedId={selectedId}
                    showForm={showForm}
                    onSelect={() => handleSelectAddress(addr)}
                    onEdit={() => setEditingAddr(addr)}
                    onDelete={() => handleDeleteAddr(addr.id)}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowForm((p) => !p);
                  setShowOtherAddresses(false);
                  setSelectedId(null);
                }}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed py-3 text-sm font-bold transition-all
                  border-primary-400 bg-primary-50 text-primary-600 hover:border-primary-500 hover:bg-primary-100"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add New Address
              </button>
            </>
          </div>
        </div>
      )}

      {/* ── New address form ── */}
      {showForm && (
        <AddressForm onSuccess={handleSaveSuccess} onCancel={handleCancelNewAddress} />
      )}

      {/* ── Pincode status for selected saved address ── */}
      {!showForm && selectedId && activePincode.length === 6 && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <PincodeStatus status={pincodeStatus} message={pincodeMessage} />
            {pincodeStatus === 'idle' || pincodeStatus === 'error' || pincodeStatus === 'not_serviceable' ? (
              <button
                type="button"
                onClick={() => checkPincode(activePincode)}
                disabled={pincodeChecking}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-50"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {pincodeChecking ? 'Checking...' : 'Re-check'}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* ── Continue ── */}
      <form
        onSubmit={handleSubmit}
        noValidate
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      >
        {!selectedId && !showForm && (
          <>
            <div className="sm:col-span-2">
              <CheckoutField
                label="Address Line 1"
                id="addressLine1"
                placeholder="House / Flat no., Building, Street"
                required
                {...field("addressLine1")}
              />
            </div>
            <div className="sm:col-span-2">
              <CheckoutField
                label="Address Line 2"
                id="addressLine2"
                name="addressLine2"
                value={form.addressLine2 || ""}
                onChange={onChange}
                placeholder="Area, Colony (optional)"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <CheckoutField
                label="PIN Code"
                id="pincode"
                placeholder="411001"
                required
                {...field("pincode")}
              />
              {/* Pincode status for manual entry */}
              {form.pincode?.length === 6 && (
                <PincodeStatus
                  status={pincodeStatus}
                  message={pincodeMessage}
                />
              )}
            </div>
            <CheckoutField
              label={`City ${pincodeCityState.city && !touched.city ? "(Auto)" : "*"}`}
              id="city"
              placeholder="Pune"
              required
              {...field("city")}
              className={pincodeCityState.city && !touched.city ? "bg-emerald-50 focus:ring-emerald-500/10 focus:border-emerald-500" : ""}
            />
            <CheckoutField
              label={`State ${pincodeCityState.state && !touched.state ? "(Auto)" : "*"}`}
              id="state"
              placeholder="Maharashtra"
              required
              {...field("state")}
              className={pincodeCityState.state && !touched.state ? "bg-emerald-50 focus:ring-emerald-500/10 focus:border-emerald-500" : ""}
            />
          </>
        )}

        <div className="pt-2 sm:col-span-2">
          <button
            type="submit"
            disabled={blockedByPincode || pincodeChecking}
            className={`w-full rounded-2xl py-4 text-base font-bold text-white shadow-lg transition-all active:scale-95 ${
              blockedByPincode
                ? "cursor-not-allowed bg-gray-300 shadow-none"
                : pincodeChecking
                  ? "cursor-wait bg-primary-400 shadow-none"
                  : "bg-primary-600 hover:-translate-y-0.5 hover:bg-primary-700"
            }`}
          >
            {blockedByPincode
              ? "⚠ Delivery not available for this pincode"
              : pincodeChecking
                ? "Checking pincode…"
                : "Continue to Review →"}
          </button>

          {blockedByPincode && (
            <p className="mt-2 text-center text-xs text-gray-500">
              Please select a different address or{" "}
              <a
                href="/contact"
                className="font-semibold text-primary-600 hover:underline"
              >
                contact support
              </a>{" "}
              to request delivery expansion to your area.
            </p>
          )}
        </div>
      </form>
      {/* ── Edit address modal ── */}
      {editingAddr && (
        <CheckoutEditModal
          address={editingAddr}
          loading={addresses_loading}
          onSubmit={handleEditAddrSubmit}
          onCancel={() => setEditingAddr(null)}
        />
      )}
    </div>
  );
};

export default AddressStep;
